import React, {FC} from 'react';
import styled, {useTheme} from 'styled-components';

import {findIndicatedTicketStatus} from '../../../../../../libs/inboxes/business/src/ticketing/conversation';
import {ConversationTeamStatusesEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {getTicketingPreferences} from '../../../../../../libs/shared/core/src/store/data/company/commonCompanySelectors';
import {Pill} from '../../../../../../libs/shared/ui/src/components/pill/pill';
import {NewPaletteColorsEnum} from '../../../../../../libs/shared/ui/src/styles/colorStyles';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {TicketStatusPill} from '../../../../../components/inboxes/ticketing/ticketStatusPill';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {AnalyticsDetailsConversation} from '../../../../../models/api/analyticsMetricDetailsModel';
import {getInboxesWithTicketingEnabled} from '../../../../../store/panels/analytics/report/analyticsPanelReportSelectors';

/*
 * Props.
 */

interface ConversationCardStatusProps {
  conversation: AnalyticsDetailsConversation;
}

/*
 * Style.
 */

const StyledPill = styled(Pill)`
  text-transform: capitalize;
  display: flex;
  align-items: center;
  line-height: ${fontSizes.tiny};
  overflow: hidden;
  margin-left: 4px;
  padding: 2.5px 5px;
`;

/*
 * Component.
 */

export const ConversationCardStatus: FC<ConversationCardStatusProps> = ({conversation}) => {
  const ticketingPreferences = useAppSelector(getTicketingPreferences);
  const ticketStatus = findIndicatedTicketStatus(ticketingPreferences, conversation);
  const inboxesWithTicketingEnabled = useAppSelector(getInboxesWithTicketingEnabled);
  const theme = useTheme();

  const isConversationInTicketInbox = conversation.inboxIds.some((inboxId) =>
    inboxesWithTicketingEnabled.some((inbox) => inbox.id === inboxId),
  );

  if (!ticketStatus || !isConversationInTicketInbox) {
    const {teamStatus} = conversation;
    const color = theme.palette[getConversationStatusColor(teamStatus)];
    return (
      <StyledPill backgroundColor={color.shade20} color={color.shade50} size={VisualSizesEnum.MEDIUM}>
        {teamStatus}
      </StyledPill>
    );
  }

  return <TicketStatusPill isCompact ticketStatus={ticketStatus} />;
};

/*
 * Helpers.
 */

function getConversationStatusColor(teamStatus: ConversationTeamStatusesEnum) {
  switch (teamStatus) {
    case ConversationTeamStatusesEnum.ARCHIVED:
      return NewPaletteColorsEnum.GREEN;
    case ConversationTeamStatusesEnum.OPEN:
      return NewPaletteColorsEnum.BLUE;
    default:
      return NewPaletteColorsEnum.RED;
  }
}
